/**
 * Format a date-only value with the CLDR-conventional layout for the
 * device's raw `navigator.language`, not the app's allowlisted locale set.
 *
 * Uses `Intl.DateTimeFormat` so every locale resolves to its native rendering
 * without going through moment's `L`/`LL` tokens (which read `moment.locale()`
 * and therefore fold every unsupported locale back to en-US visuals via
 * `constants/supported-locales.js`).
 *
 * Styles:
 *   - 'numeric'        →   en-US "06/05/2026", en-GB "05/06/2026",
 *                          en-CA "2026-06-05", de-DE "05.06.2026"
 *                          (explicit 2-digit-month/day + numeric-year options
 *                          rather than `dateStyle: 'short'` — CLDR's short
 *                          falls back to 2-digit year in some locales, which
 *                          we don't want for UK/AUS launch consistency.)
 *   - 'monthDay'       →   en-US "Jun 5", en-GB "5 Jun" (no year)
 *   - 'monthDayYear'   →   en-US "Jun 5, 2026", en-GB "5 Jun 2026"
 *   - 'weekdayMonthDay'→   en-US "Fri, Jun 5", en-GB "Fri 5 Jun" (no year)
 *
 * @param {string|Date|number} value - ISO string, Date, or epoch ms
 * @param {object} [options]
 * @param {'numeric'|'monthDay'|'monthDayYear'|'weekdayMonthDay'} [options.style='numeric']
 * @param {string} [options.timeZone] - IANA zone (e.g. "America/Los_Angeles")
 * @returns {string}
 */
function formatDate(value, { style = 'numeric', timeZone } = {}) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const locale = window.navigator.language || 'en-US';
  const options = formatOptionsFor(style);

  if (timeZone) {
    options.timeZone = timeZone;
  }

  return new Intl.DateTimeFormat(locale, options).format(date);
}

function formatOptionsFor(style) {
  switch (style) {
    case 'monthDay':
      return { month: 'short', day: 'numeric' };
    case 'monthDayYear':
      return { month: 'short', day: 'numeric', year: 'numeric' };
    case 'weekdayMonthDay':
      return { weekday: 'short', month: 'short', day: 'numeric' };
    case 'numeric':
    default:
      return { year: 'numeric', month: '2-digit', day: '2-digit' };
  }
}



export default formatDate;
